import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AccessDeniedNotice } from '../../../shared/AccessDeniedNotice';
import { EmptyState } from '../../../shared/EmptyState';
import { RetryableError } from '../../../shared/RetryableError';
import { ConfirmDialog } from '../../job-roles/components/ConfirmDialog';
import { RowActionsMenu, type RowAction } from '../../job-roles/components/RowActionsMenu';
import { bdErrorMessage, isForbidden } from '../bdErrors';
import { ANNUAL_BONUS_NOTE, ANNUAL_BONUS_PLAN_CODE, isOtherCatalogItem } from '../catalogData';
import { BENEFIT_CATEGORY_BLURBS, BENEFIT_CATEGORY_CODES, BENEFIT_CATEGORY_LABELS, type BenefitCategoryCode } from '../categories';
import { CatalogList, type CatalogRow } from '../components/CatalogList';
import { DefinitionFormDialog } from '../components/DefinitionFormDialog';
import { ElectionsDialog } from '../components/ElectionsDialog';
import { EnrollmentDialog } from '../components/EnrollmentDialog';
import { ListFilters, Pager, SortableHeader, StatusBadge, type FilterState } from '../components/ListControls';
import { PlanFormDialog, type PlanDialogMode } from '../components/PlanFormDialog';
import { formatDate } from '../dates';
import { benefitsGateway } from '../index';
import { costSummary } from '../planSummary';
import { useDebounced } from '../useDebounced';
import {
  createsPayrollDeductionLabels, type BenefitCatalogItem, type BenefitPlan, type Paged, type PlanSort,
} from '../types';
import '../../job-roles/job-roles.css';
import '../benefits-deductions.css';

type PlansState = { kind: 'loading' } | { kind: 'ready'; data: Paged<BenefitPlan> } | { kind: 'forbidden' } | { kind: 'error'; message: string };
type CatalogState = { kind: 'loading' } | { kind: 'ready'; items: BenefitCatalogItem[] } | { kind: 'forbidden' } | { kind: 'error'; message: string };

type Dialog =
  | { kind: 'plan'; mode: PlanDialogMode; planId?: string; catalogType?: BenefitCatalogItem | null }
  | { kind: 'deactivate'; plan: BenefitPlan }
  | { kind: 'enroll'; planId: string }
  | { kind: 'elections'; planId: string };

const CATEGORY_OPTIONS = BENEFIT_CATEGORY_CODES.map((c) => ({ value: c, label: BENEFIT_CATEGORY_LABELS[c] }));

function catalogRow(item: BenefitCatalogItem): CatalogRow {
  const tags: string[] = [];
  if (item.leaveLinked) tags.push('Managed in Time and Leave');
  else tags.push(createsPayrollDeductionLabels[item.createsPayrollDeduction]);
  return {
    code: item.code, name: item.name, description: item.description, isHidden: item.isHidden, tags,
    note: item.code === ANNUAL_BONUS_PLAN_CODE ? ANNUAL_BONUS_NOTE : null, opensCustomForm: isOtherCatalogItem(item),
  };
}

/** One page component for the seven benefit category leaves: the route decides the category. Remounts per category so no filter or dialog leaks across. */
export function BenefitCategoryPage({ category }: { category: BenefitCategoryCode }) {
  return <CategoryPage key={category} category={category} />;
}

/**
 * A People > Benefits category: the system catalog for the category (add from the catalog, prefilled, or add a custom benefit; hide or show a type for this
 * company), and the company's plans with search, filters, sortable columns and paging. Every permission decision is the server's: a 403 on a read shows the
 * no-access notice, and a 403 on a write turns the page read-only for the rest of the visit. Adding a benefit never creates a payroll deduction by itself.
 */
function CategoryPage({ category }: { category: BenefitCategoryCode }) {
  const label = BENEFIT_CATEGORY_LABELS[category];
  const [filters, setFilters] = useState<FilterState>({ search: '', category, status: 'all', effectiveOn: '' });
  const search = useDebounced(filters.search.trim());
  const [sort, setSort] = useState<PlanSort>('name');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [plans, setPlans] = useState<PlansState>({ kind: 'loading' });
  const [catalog, setCatalog] = useState<CatalogState>({ kind: 'loading' });
  const [showHidden, setShowHidden] = useState(false);
  const [nonce, setNonce] = useState(0);
  const [catalogNonce, setCatalogNonce] = useState(0);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [readOnly, setReadOnly] = useState(false);
  const [busyCode, setBusyCode] = useState<string | null>(null);
  const [busyPlan, setBusyPlan] = useState<string | null>(null);
  const [deactivating, setDeactivating] = useState(false);
  const [deactivateError, setDeactivateError] = useState<string | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  // A new search or filter starts at page 1.
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { setPage(1); }, [search, filters.category, filters.status, filters.effectiveOn]);

  useEffect(() => {
    const controller = new AbortController();
    benefitsGateway
      .listPlans({
        search: search || undefined, category: filters.category || undefined, status: filters.status, effectiveOn: filters.effectiveOn || undefined, sort, page, pageSize,
      }, controller.signal)
      .then((data) => { if (!controller.signal.aborted) setPlans({ kind: 'ready', data }); })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setPlans(isForbidden(err) ? { kind: 'forbidden' } : { kind: 'error', message: bdErrorMessage(err, 'Could not load the plans.') });
      });
    return () => controller.abort();
  }, [search, filters.category, filters.status, filters.effectiveOn, sort, page, pageSize, nonce]);

  useEffect(() => {
    const controller = new AbortController();
    benefitsGateway.listCatalog({ category, includeHidden: showHidden }, controller.signal)
      .then((items) => { if (!controller.signal.aborted) setCatalog({ kind: 'ready', items }); })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setCatalog(isForbidden(err) ? { kind: 'forbidden' } : { kind: 'error', message: bdErrorMessage(err, 'Could not load the benefit types.') });
      });
    return () => controller.abort();
  }, [category, showHidden, catalogNonce]);

  const reload = useCallback(() => { setPlans((p) => (p.kind === 'ready' ? p : { kind: 'loading' })); setNonce((n) => n + 1); }, []);
  const reloadCatalog = useCallback(() => setCatalogNonce((n) => n + 1), []);
  const markReadOnly = useCallback(() => setReadOnly(true), []);

  const forbidden = plans.kind === 'forbidden' && catalog.kind === 'forbidden';
  const filtersActive = filters.search.trim() !== '' || filters.category !== category || filters.status !== 'all' || filters.effectiveOn !== '';
  const canWrite = !readOnly && !forbidden;
  // The category column only earns its width when the list spans categories.
  const allCategories = filters.category === '';

  function clearFilters() {
    setFilters({ search: '', category, status: 'all', effectiveOn: '' });
  }

  function saved(plan: BenefitPlan, mode: 'create' | 'edit') {
    setDialog(null);
    setNotice(mode === 'create'
      ? `${plan.name} was added.${plan.configurationStatus !== 'Complete' ? ' It needs tax review before employees can be enrolled in it.' : ''}`
      : `${plan.name} was saved. The change applies from now on: earlier elections are not rewritten.`);
    reload();
  }

  async function toggleHidden(row: CatalogRow) {
    setBusyCode(row.code);
    setActionError(null);
    setNotice(null);
    try {
      if (row.isHidden) await benefitsGateway.showCatalogType(row.code); else await benefitsGateway.hideCatalogType(row.code);
      setNotice(`${row.name} is now ${row.isHidden ? 'shown' : 'hidden'} for your company. Nothing was deleted.`);
      reloadCatalog();
    } catch (err) {
      if (isForbidden(err)) markReadOnly();
      setActionError(bdErrorMessage(err, 'Could not change this benefit type.'));
    } finally {
      setBusyCode(null);
    }
  }

  async function reactivate(plan: BenefitPlan) {
    setBusyPlan(plan.id);
    setActionError(null);
    setNotice(null);
    try {
      await benefitsGateway.reactivatePlan(plan.id);
      setNotice(`${plan.name} is active again.`);
      reload();
    } catch (err) {
      if (isForbidden(err)) markReadOnly();
      setActionError(bdErrorMessage(err, 'Could not reactivate this plan.'));
    } finally {
      setBusyPlan(null);
    }
  }

  async function deactivate(plan: BenefitPlan) {
    setDeactivating(true);
    setDeactivateError(null);
    try {
      await benefitsGateway.deactivatePlan(plan.id);
      setDialog(null);
      setNotice(`${plan.name} was deactivated. It was not deleted, and its elections are unchanged.`);
      reload();
    } catch (err) {
      if (isForbidden(err)) markReadOnly();
      setDeactivateError(bdErrorMessage(err, 'Could not deactivate this plan.'));
    } finally {
      setDeactivating(false);
    }
  }

  function actionsFor(plan: BenefitPlan): RowAction[] {
    const actions: RowAction[] = [{ key: 'view', label: 'View', onSelect: () => setDialog({ kind: 'plan', mode: 'view', planId: plan.id }) }];
    if (canWrite) {
      actions.push({ key: 'edit', label: 'Edit', onSelect: () => setDialog({ kind: 'plan', mode: 'edit', planId: plan.id }) });
      if (plan.isActive) actions.push({ key: 'deactivate', label: 'Deactivate', danger: true, onSelect: () => { setDeactivateError(null); setDialog({ kind: 'deactivate', plan }); } });
      else actions.push({ key: 'reactivate', label: 'Reactivate', onSelect: () => void reactivate(plan) });
      if (plan.isActive && !plan.leaveLinked) actions.push({ key: 'enroll', label: 'Enroll an employee', onSelect: () => setDialog({ kind: 'enroll', planId: plan.id }) });
    }
    if (!plan.leaveLinked) actions.push({ key: 'elections', label: 'View elections', onSelect: () => setDialog({ kind: 'elections', planId: plan.id }) });
    return actions;
  }

  const data = plans.kind === 'ready' ? plans.data : null;
  const firstIndex = data ? (data.page - 1) * data.pageSize : 0;
  const catalogRows = catalog.kind === 'ready' ? catalog.items.map(catalogRow) : [];

  return (
    <section className="card card--wide jr-page bd-page">
      <div className="section-header">
        <h2 ref={headingRef} tabIndex={-1}>{label}</h2>
        {canWrite && (
          <div className="jr-actions">
            <button type="button" className="jr-btn jr-btn--primary" onClick={() => setDialog({ kind: 'plan', mode: 'create', catalogType: null })}>
              <span aria-hidden="true">+ </span>Add Custom Benefit
            </button>
          </div>
        )}
      </div>
      <p className="muted bd-lead">{BENEFIT_CATEGORY_BLURBS[category]}</p>

      {category === 'leave-wellbeing' && (
        <p className="bd-notice bd-notice--info" role="note" data-testid="leave-note">
          Time and Leave owns leave balances, accruals and requests. A leave benefit here only describes the program, and it carries no payroll contribution.{' '}
          Manage balances in <Link to="/time-off">People &gt; Employees &gt; Time Off</Link>.
        </p>
      )}
      {readOnly && (
        <p className="jr-readonly" role="status" data-testid="read-only-notice">
          <span aria-hidden="true">{'🔒'} </span>You can view benefits but not change them. Ask a company administrator if you need to add, edit or deactivate a benefit.
        </p>
      )}
      {notice && (
        <p className="jr-notice jr-notice--ok" role="status">
          <span aria-hidden="true">{'✓'} </span>{notice}{' '}
          <button type="button" className="link" onClick={() => { setNotice(null); headingRef.current?.focus(); }}>Dismiss</button>
        </p>
      )}
      {actionError && <p className="jr-error jr-error--block" role="alert">{actionError}</p>}

      {forbidden ? (
        <AccessDeniedNotice requiredPermissionLabel="permission to view benefits (benefitcodes.read)" />
      ) : (
        <>
          <h3>Benefit types in this category</h3>
          {catalog.kind === 'loading' && <p className="muted" role="status">Loading benefit types{'…'}</p>}
          {catalog.kind === 'error' && <RetryableError message={catalog.message} onRetry={reloadCatalog} />}
          {catalog.kind === 'forbidden' && <AccessDeniedNotice requiredPermissionLabel="permission to view benefits (benefitcodes.read)" />}
          {catalog.kind === 'ready' && (
            <CatalogList
              idPrefix="bd-catalog" noun="benefit" rows={catalogRows} readOnly={!canWrite} busyCode={busyCode} showHidden={showHidden} onShowHidden={setShowHidden}
              emptyMessage={category === 'other-custom' ? 'This category has no starter types. Choose Add Custom Benefit to create your own.' : 'Every type in this category is hidden. Show a hidden type to add it.'}
              onAdd={(row) => setDialog({ kind: 'plan', mode: 'create', catalogType: row.opensCustomForm ? null : catalog.items.find((c) => c.code === row.code) ?? null })}
              onToggleHidden={(row) => void toggleHidden(row)}
            >
              <p className="bd-help">Adding a type from the catalog fills in its name and description. Selecting a benefit does not automatically create a payroll deduction.</p>
            </CatalogList>
          )}

          <h3>Your plans</h3>
          <ListFilters
            idPrefix="bd-plans" noun="plans" state={filters} categories={CATEGORY_OPTIONS} active={filtersActive} onClear={clearFilters}
            onChange={(patch) => setFilters((f) => ({ ...f, ...patch }))}
          />
          {plans.kind === 'loading' && <p className="muted" role="status">Loading plans{'…'}</p>}
          {plans.kind === 'error' && <RetryableError message={plans.message} onRetry={reload} />}
          {plans.kind === 'forbidden' && <AccessDeniedNotice requiredPermissionLabel="permission to view benefits (benefitcodes.read)" />}

          {data && data.totalCount === 0 && !filtersActive && (
            <EmptyState
              message={`No ${label} plans yet. Add one from the types above, or add a custom benefit.`}
              action={canWrite ? <button type="button" className="jr-btn jr-btn--primary" onClick={() => setDialog({ kind: 'plan', mode: 'create', catalogType: null })}>Add Custom Benefit</button> : undefined}
            />
          )}
          {data && data.totalCount === 0 && filtersActive && (
            <EmptyState message="No plans match your search or filters." action={<button type="button" className="jr-btn jr-btn--secondary" onClick={clearFilters}>Clear filters</button>} />
          )}

          {data && data.totalCount > 0 && (
            <>
              <p className="muted" aria-live="polite" data-testid="plans-count">
                Showing {firstIndex + 1}{'–'}{firstIndex + data.items.length} of {data.totalCount} {data.totalCount === 1 ? 'plan' : 'plans'}
              </p>
              <div className="jr-table-scroll" role="region" aria-label="Benefit plans table" tabIndex={0}>
                <table className="jr-table jr-table--actions bd-table">
                  <caption className="jr-sr-only">Benefit plans for this company</caption>
                  <thead>
                    <tr>
                      <SortableHeader label="Name" ascValue="name" descValue="-name" sort={sort} onSort={(s) => { setSort(s); setPage(1); }} />
                      {allCategories && <SortableHeader label="Category" ascValue="category" sort={sort} onSort={(s) => { setSort(s); setPage(1); }} />}
                      <th scope="col">Provider</th>
                      <SortableHeader label="Effective" ascValue="effectiveStart" descValue="-effectiveStart" sort={sort} onSort={(s) => { setSort(s); setPage(1); }} />
                      <th scope="col">Cost</th>
                      <th scope="col" className="num">Enrolled</th>
                      <SortableHeader label="Status" ascValue="status" sort={sort} onSort={(s) => { setSort(s); setPage(1); }} />
                      <th scope="col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((plan) => {
                      const dash = <span className="muted" title="Not set">{'—'}</span>;
                      return (
                        <tr key={plan.id} aria-busy={busyPlan === plan.id || undefined}>
                          <th scope="row" className="jr-table__title">
                            <button type="button" className="link" onClick={() => setDialog({ kind: 'plan', mode: 'view', planId: plan.id })}>{plan.name}</button>
                            <span className="bd-table-note">{plan.shortCode} · {plan.isCustom ? 'Custom' : 'Catalog'}</span>
                          </th>
                          {allCategories && <td>{BENEFIT_CATEGORY_LABELS[plan.categoryCode] ?? plan.categoryCode}</td>}
                          <td>{plan.providerName ?? dash}</td>
                          <td className="jr-nowrap">{plan.effectiveStartDate ? formatDate(plan.effectiveStartDate) : dash}{plan.effectiveEndDate ? ` to ${formatDate(plan.effectiveEndDate)}` : ''}</td>
                          <td>{costSummary(plan)}</td>
                          <td className="num">{plan.enrolledCount}</td>
                          <td><StatusBadge isActive={plan.isActive} configurationStatus={plan.configurationStatus} /></td>
                          <td><RowActionsMenu label={`Actions for ${plan.name}`} actions={actionsFor(plan)} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <Pager page={data.page} pageSize={data.pageSize} totalCount={data.totalCount} onPage={setPage} onPageSize={(n) => { setPageSize(n); setPage(1); }} />
            </>
          )}
        </>
      )}

      {dialog?.kind === 'plan' && (
        <PlanFormDialog
          mode={dialog.mode}
          category={category}
          {...(dialog.planId ? { planId: dialog.planId } : {})}
          catalogType={dialog.catalogType ?? null}
          onClose={() => setDialog(null)}
          onSaved={saved}
          onForbidden={markReadOnly}
          renderCreateDeduction={({ onCreated, onClose }) => (
            <DefinitionFormDialog mode="create" category="benefit-premiums" onClose={onClose} onSaved={(definition) => onCreated(definition)} onForbidden={markReadOnly} />
          )}
        />
      )}
      {dialog?.kind === 'deactivate' && (
        <ConfirmDialog
          title="Deactivate this plan?" confirmLabel="Deactivate" busy={deactivating} error={deactivateError}
          onCancel={() => setDialog(null)} onConfirm={() => void deactivate(dialog.plan)}
        >
          <p className="bd-modal-note">
            Deactivating {dialog.plan.name} keeps it in the list and keeps its elections, but it cannot take new elections. A plan is never deleted. Deactivating does not change payroll.
          </p>
        </ConfirmDialog>
      )}
      {dialog?.kind === 'enroll' && (
        <EnrollmentDialog initialPlanId={dialog.planId} onClose={() => setDialog(null)} onChanged={() => reload()} onForbidden={markReadOnly} />
      )}
      {dialog?.kind === 'elections' && (
        <ElectionsDialog initialPlanId={dialog.planId} readOnly={readOnly} onClose={() => setDialog(null)} onChanged={() => reload()} onForbidden={markReadOnly} />
      )}
    </section>
  );
}
